import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Layers, FileChartPie, Sparkles, ArrowUpRight } from 'lucide-react';
import featureImage from '../assets/featureimage.png';
import feat1Dark from '../assets/feat-assistants-dark.png';
import feat1Light from '../assets/feat-assistants-light.png';
import feat2Dark from '../assets/feat-pdf-dark.png';
import feat2Light from '../assets/feat-pdf-light.png';
import feat3Dark from '../assets/feat-object-dark.png';
import feat3Light from '../assets/feat-object-light.png';
import { useTheme } from '../context/ThemeContext';

const Features = () => {
  const { isDark } = useTheme();

  return (
    <>
      <Helmet>
        <title>Vuzo | AI Productivity in Your Browser</title>
        <meta name="description" content="Get unlimited access to advanced AI tools with Vuzo: your all-in-one smart assistant powered by GPT-5.4 Pro, Gemini 2.5 Flash, Claude Sonnet, and more. Start for free at chat.vuzo.com." />
      </Helmet>

      {/* Hero */}
      <div className="relative z-10 w-full py-16" style={{
        background: isDark
          ? 'radial-gradient(circle at center 150%, #14553E 0%, #000000 60%)'
          : 'radial-gradient(circle at center 150%, rgba(23,119,85,0.08) 0%, #ffffff 60%)',
      }}>
        <div className="max-w-[1280px] mx-auto px-0 md:px-4">
          <div className="flex flex-col lg:flex-row gap-8 items-center">
            <div className="space-y-6 w-full lg:w-[60%] text-center lg:text-left">
              <span className={`inline-flex items-center text-sm font-medium px-4 py-2 rounded-full ${
                isDark ? 'bg-[#104B303A] text-[#27F5AB]' : 'bg-[#177755]/8 text-[#177755] border border-[#177755]/20'
              }`}>
                Vuzo: AI Chatbot
              </span>
              <h1 className={`text-[48px] font-semibold leading-tight ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
                Unleash Unlimited<br />Knowledge with{' '}
                <span className={isDark ? 'text-[#28F5AB]' : 'text-[#177755]'}>Vuzo</span>
              </h1>
              <p className={`text-[18px] leading-relaxed lg:max-w-2xl ${isDark ? 'text-gray-300' : 'text-[#475569]'}`}>
                Begin your AI journey with us at Vuzo. We're dedicated to providing you with state-of-the-art tools,
                expert insights, and customized resources to excel in today's AI-powered landscape.
              </p>
              <a
                href="https://chat.vuzo.com"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl text-[16px] font-semibold transition-all duration-200 shadow-md ${
                  isDark
                    ? 'bg-[#28F5AB] text-[#0A0A0A] hover:bg-[#1de098]'
                    : 'bg-[#177755] text-white hover:bg-[#0f5b3e]'
                }`}
              >
                Start for Free
                <ArrowUpRight className="w-5 h-5" />
              </a>
            </div>
            <div className="w-full lg:w-[60%]">
              <img src={featureImage} alt="Feature" className="w-full object-contain" />
            </div>
          </div>
        </div>
      </div>

      {/* Feature grid */}
      <div className={`w-full ${isDark ? 'bg-[#0E1314]' : 'bg-[#EFF6F2]'}`}>
        <div className="max-w-[1280px] mx-auto pt-8 pb-20 px-4">
          <div className="text-center mb-12">
            <span className={`inline-flex items-center text-sm font-medium px-4 py-2 ${isDark ? 'text-[#27F5AB]' : 'text-[#177755]'}`}>
              Powerful Features
            </span>
            <p className={`text-[48px] md:text-[56px] leading-tight font-medium ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>
              <span className={isDark ? 'text-[#28F5AB]' : 'text-[#177755]'}>Vuzo</span> has these<br />great features
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-28">
            {[
              { imgDark: feat1Dark, imgLight: feat1Light, Icon: Layers, title: 'Multiple AI Assistants, One Platform', desc: 'Switch between AI experts like Writer, Lawyer, Teacher, or Designer and get expert answers fast.' },
              { imgDark: feat2Dark, imgLight: feat2Light, Icon: FileChartPie, title: 'From PDFs to Insights in Seconds', desc: 'Upload a file and instantly get summaries, analysis, and key insights without reading it yourself.' },
              { imgDark: feat3Dark, imgLight: feat3Light, Icon: Sparkles, title: 'Identify Objects in a Snap', desc: 'Point your camera and let AI instantly recognize objects, places, products, and scenes.' },
            ].map(({ imgDark, imgLight, Icon, title, desc }) => (
              <div key={title} className={`rounded-2xl p-6 border ${
                isDark ? 'bg-[#15191A] border-[#1F3028]' : 'bg-white border-[#E8F5EF] shadow-[0_4px_24px_rgba(0,0,0,0.06)]'
              }`}>
                <div className="w-full mb-6 rounded-xl overflow-hidden">
                  <img src={isDark ? imgDark : imgLight} alt={title} className="w-full" />
                </div>
                <div className="flex items-start gap-3 mb-3">
                  <Icon className={`w-6 h-6 shrink-0 mt-0.5 ${isDark ? 'text-[#28F5AB]' : 'text-[#177755]'}`} />
                  <h3 className={`text-[18px] font-semibold leading-snug ${isDark ? 'text-white' : 'text-[#0F172A]'}`}>{title}</h3>
                </div>
                <p className={`text-[14px] leading-relaxed ${isDark ? 'text-gray-300' : 'text-[#64748B]'}`}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Features;
